import { Head } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import type { SeoMeta } from "@/types"

const EFFECTIVE_DATE = "13 February 2026"
const COMPANY_NAME = "QuiteRight"
const COMPANY_ENTITY = "QuiteRight Ltd"
const CONTACT_EMAIL = "team@quiteright.co.uk"
const DPO_EMAIL = "team@quiteright.co.uk"
const WEBSITE = "quiteright.co.uk"

export default function Privacy({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta name="robots" content="noindex, follow" />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
      </Head>

      <article className="px-6 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">

          {/* Header */}
          <header className="mb-16 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Legal</p>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal md:text-5xl">
              Privacy Policy<span className="text-red">.</span>
            </h1>
            <p className="mt-4 text-sm text-warm-grey">
              Effective date: {EFFECTIVE_DATE}
            </p>
          </header>

          {/* Body */}
          <div className="prose-brand space-y-10 text-sm leading-relaxed text-slate [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-charcoal [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-charcoal [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_strong]:font-semibold [&_strong]:text-charcoal [&_a]:text-red [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-deep-red">

            {/* 1 */}
            <section>
              <h2>1. Introduction</h2>
              <p>
                {COMPANY_ENTITY} (&ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;, &ldquo;<strong>our</strong>&rdquo;, or &ldquo;<strong>{COMPANY_NAME}</strong>&rdquo;) is committed to protecting your privacy and handling your personal data responsibly.
              </p>
              <p>
                This Privacy Policy explains how we collect, use, store, share, and protect your personal data when you access or use the {COMPANY_NAME} platform, website ({WEBSITE}), and related services (the &ldquo;<strong>Service</strong>&rdquo;).
              </p>
              <p>
                We are the data controller for the personal data described in this policy. Our registered address is in England and Wales. For data protection queries, contact us at <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2>2. Information We Collect</h2>
              <h3>2.1 Information you provide</h3>
              <ul>
                <li><strong>Account information:</strong> Name, email address, password, company/agency name, and role when you register for an Account.</li>
                <li><strong>Billing information:</strong> Payment card details and billing address, processed by our payment processor Stripe. We do not store full card numbers on our servers.</li>
                <li><strong>Profile and brand information:</strong> Agency logo, brand colours, and other customisation details you provide.</li>
                <li><strong>Listing data:</strong> Property details, photographs, descriptions, prices, and features you import or enter into the Service.</li>
                <li><strong>Content you create:</strong> Video scripts, captions, and any text you write or edit within the Service.</li>
                <li><strong>Communications:</strong> Information you provide when you contact us for support, feedback, or enquiries.</li>
              </ul>
              <h3>2.2 Information collected automatically</h3>
              <ul>
                <li><strong>Usage data:</strong> Pages visited, features used, actions taken, timestamps, session duration, and interaction patterns within the Service.</li>
                <li><strong>Device and browser information:</strong> IP address, browser type, operating system, device type, screen resolution, and language preferences.</li>
                <li><strong>Cookies and similar technologies:</strong> We use cookies, local storage, and similar tracking technologies as described in Section 8 below.</li>
                <li><strong>Log data:</strong> Server logs including access times, referring URLs, and error reports.</li>
              </ul>
              <h3>2.3 Information from third parties</h3>
              <ul>
                <li><strong>Social media platforms:</strong> When you connect social media accounts (Instagram, TikTok, YouTube, Facebook, LinkedIn), we receive access tokens and basic profile information (username, profile picture) necessary to publish on your behalf. We do not access your private messages or personal feeds.</li>
                <li><strong>Property portals:</strong> When you import a listing, we extract publicly available property data from portals such as Rightmove, Zoopla, and OnTheMarket.</li>
                <li><strong>Stripe:</strong> Subscription status, payment history, and billing events.</li>
              </ul>
            </section>

            {/* 3 */}
            <section>
              <h2>3. How We Use Your Information</h2>
              <p>We use your personal data for the following purposes:</p>
              <ul>
                <li><strong>Providing the Service:</strong> Creating and managing your Account, processing listings, generating videos, publishing to social platforms, and delivering the features you use.</li>
                <li><strong>Billing and payments:</strong> Processing subscription payments, issuing invoices, and managing billing enquiries.</li>
                <li><strong>Communications:</strong> Sending transactional emails (account confirmations, password resets, billing receipts), service announcements, and - where you have opted in - marketing communications.</li>
                <li><strong>Improving the Service:</strong> Analysing usage patterns, diagnosing technical issues, and developing new features. We use aggregated, anonymised data wherever possible.</li>
                <li><strong>Security:</strong> Detecting and preventing fraud, abuse, and security threats. Enforcing our <a href="/terms">Terms &amp; Conditions</a>.</li>
                <li><strong>Legal compliance:</strong> Complying with applicable laws, regulations, and legal processes.</li>
              </ul>
            </section>

            {/* 4 */}
            <section>
              <h2>4. Lawful Basis for Processing</h2>
              <p>Under the UK GDPR, we rely on the following lawful bases:</p>
              <ul>
                <li><strong>Contract:</strong> Processing necessary to perform our contract with you (providing the Service, managing your Account, processing payments).</li>
                <li><strong>Legitimate interests:</strong> Processing necessary for our legitimate interests, including improving the Service, ensuring security, and conducting analytics - provided these interests are not overridden by your rights.</li>
                <li><strong>Consent:</strong> Where you have given explicit consent, such as for marketing communications. You may withdraw consent at any time.</li>
                <li><strong>Legal obligation:</strong> Processing necessary to comply with legal obligations, such as tax and financial reporting requirements.</li>
              </ul>
            </section>

            {/* 5 */}
            <section>
              <h2>5. Data Sharing and Disclosure</h2>
              <p>We do not sell your personal data. We share personal data only in the following circumstances:</p>
              <h3>5.1 Service providers</h3>
              <p>
                We use trusted third-party service providers to operate the Service. These providers process data on our behalf and are contractually obligated to protect your data:
              </p>
              <ul>
                <li><strong>Hosting and infrastructure:</strong> Amazon Web Services (AWS) - servers, storage, and video rendering infrastructure.</li>
                <li><strong>Payment processing:</strong> Stripe - subscription billing and payment handling.</li>
                <li><strong>Email delivery:</strong> Transactional email services for account notifications.</li>
                <li><strong>Analytics:</strong> Anonymised usage analytics to improve the Service.</li>
                <li><strong>Voiceover generation:</strong> Third-party text-to-speech services for automated voiceover narration.</li>
              </ul>
              <h3>5.2 Social media platforms</h3>
              <p>
                When you publish a video via the Service, we transmit the video, caption, and associated metadata to the social media platforms you have connected. This data is then governed by the respective platform&rsquo;s privacy policy.
              </p>
              <h3>5.3 Legal requirements</h3>
              <p>
                We may disclose personal data where required by law, regulation, legal process, or governmental request, or where we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
              </p>
              <h3>5.4 Business transfers</h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your personal data may be transferred as part of the transaction. We will notify you of any such transfer and any choices you may have regarding your data.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2>6. Data Retention</h2>
              <p>We retain personal data for as long as necessary to fulfil the purposes outlined in this policy:</p>
              <ul>
                <li><strong>Account data:</strong> Retained for the duration of your Account and for 30 days following Account closure, after which it is deleted.</li>
                <li><strong>Billing records:</strong> Retained for 7 years after the end of the financial year in which the transaction took place, as required by HMRC regulations.</li>
                <li><strong>Generated content:</strong> Videos, scripts, and captions are retained for the duration of your Account. Upon Account closure, Generated Content is deleted after a 30-day grace period.</li>
                <li><strong>Usage and analytics data:</strong> Retained in anonymised form indefinitely for service improvement purposes. Identifiable usage data is retained for up to 24 months.</li>
                <li><strong>Support communications:</strong> Retained for up to 3 years after the enquiry is resolved.</li>
              </ul>
            </section>

            {/* 7 */}
            <section>
              <h2>7. International Data Transfers</h2>
              <p>
                Your personal data is primarily stored and processed within the United Kingdom and the European Economic Area (EEA). Where data is transferred outside the UK/EEA (for example, to service providers in the United States), we ensure appropriate safeguards are in place, including:
              </p>
              <ul>
                <li>UK International Data Transfer Agreements (IDTAs) or EU Standard Contractual Clauses (SCCs);</li>
                <li>Adequacy decisions by the UK Secretary of State or the European Commission; or</li>
                <li>Other legally recognised transfer mechanisms under the UK GDPR.</li>
              </ul>
            </section>

            {/* 8 */}
            <section>
              <h2>8. Cookies and Tracking Technologies</h2>
              <h3>8.1 What we use</h3>
              <ul>
                <li><strong>Essential cookies:</strong> Required for the Service to function (session management, authentication, security). These cannot be disabled.</li>
                <li><strong>Functional cookies:</strong> Remember your preferences (language, theme, layout) to improve your experience.</li>
                <li><strong>Analytics cookies:</strong> Help us understand how the Service is used so we can improve it. These collect anonymised data only.</li>
              </ul>
              <h3>8.2 Managing cookies</h3>
              <p>
                You can manage cookie preferences through your browser settings. Disabling essential cookies may prevent the Service from functioning correctly. We do not use advertising or third-party tracking cookies.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2>9. Data Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul>
                <li>Encryption of data in transit (TLS 1.2+) and at rest;</li>
                <li>Regular security assessments and penetration testing;</li>
                <li>Access controls and role-based permissions;</li>
                <li>Secure password hashing;</li>
                <li>Regular backups and disaster recovery procedures; and</li>
                <li>Employee security awareness training.</li>
              </ul>
              <p>
                While we take reasonable precautions, no method of transmission or storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2>10. Your Rights</h2>
              <p>Under the UK GDPR and Data Protection Act 2018, you have the following rights:</p>
              <ul>
                <li><strong>Right of access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Right to rectification:</strong> Request correction of inaccurate or incomplete personal data.</li>
                <li><strong>Right to erasure:</strong> Request deletion of your personal data where there is no compelling reason for continued processing.</li>
                <li><strong>Right to restrict processing:</strong> Request that we limit how we use your data in certain circumstances.</li>
                <li><strong>Right to data portability:</strong> Receive your personal data in a structured, commonly used, machine-readable format.</li>
                <li><strong>Right to object:</strong> Object to processing based on legitimate interests, including direct marketing.</li>
                <li><strong>Rights related to automated decision-making:</strong> You have the right not to be subject to decisions based solely on automated processing that produce legal or similarly significant effects.</li>
              </ul>
              <p>
                To exercise any of these rights, contact us at <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>. We will respond within one calendar month. In complex cases, we may extend this by a further two months, and we will inform you if this is necessary.
              </p>
              <p>
                If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner&rsquo;s Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2>11. Children&rsquo;s Privacy</h2>
              <p>
                The Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal data from children. If you become aware that a child has provided us with personal data, please contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will take steps to delete such data.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2>12. Third-Party Links</h2>
              <p>
                The Service may contain links to third-party websites or services (including social media platforms and property portals). We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies before providing any personal data.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2>13. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or by posting a notice within the Service at least 14 days before the changes take effect. The &ldquo;Effective date&rdquo; at the top of this page indicates when the policy was last revised. Your continued use of the Service after any changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* 14 */}
            <section>
              <h2>14. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <ul>
                <li><strong>Data protection enquiries:</strong> <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a></li>
                <li><strong>General enquiries:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
                <li><strong>Company:</strong> {COMPANY_ENTITY}</li>
                <li><strong>Website:</strong> <a href={`https://${WEBSITE}`}>{WEBSITE}</a></li>
              </ul>
            </section>

          </div>
        </div>
      </article>
    </PublicLayout>
  )
}
